/**
 * WEGO Favorites App - 商品詳細ページお気に入り機能
 * 商品詳細ページ(/products/*)のお気に入りボタン制御
 * Version: 2026-01-28-v2 (塗りつぶしアイコン対応)
 */

(function () {
  "use strict";

  const API_BASE = "/apps/wego-favorite/api/favorites";
  const COUNT_API_BASE = "/apps/wego-favorite/api/favorites-count";
  const CACHE_DURATION = 5 * 60 * 1000; // 5分

  // ロールバック用に元のカウント値を保持
  let originalCountMap = {};

  // ポップアップ表示時のスクロール位置を保存
  const popupState = {
    oldPosition: 0,
  };

  /**
   * 開発環境かどうかを判定
   */
  function isDevEnvironment() {
    const host = window.location.host;
    return (
      host.includes("127.0.0.1") ||
      host.includes("localhost") ||
      host.includes("dev-thr-favorit")
    );
  }

  /**
   * 顧客IDを取得
   */
  function getCustomerId() {
    if (window.ShopifyCustomer && window.ShopifyCustomer.id) {
      const id = String(window.ShopifyCustomer.id);
      return /^\d+$/.test(id) ? `gid://shopify/Customer/${id}` : id;
    }

    if (window.__st && window.__st.cid) {
      const id = String(window.__st.cid);
      return /^\d+$/.test(id) ? `gid://shopify/Customer/${id}` : id;
    }

    const metaCustomerId = document.querySelector(
      'meta[name="shopify-customer-id"]',
    );
    if (metaCustomerId && metaCustomerId.content) {
      const id = metaCustomerId.content;
      return /^\d+$/.test(id) ? `gid://shopify/Customer/${id}` : id;
    }

    const button = document.querySelector("[data-customer-id]");
    if (button && button.dataset.customerId) {
      const id = button.dataset.customerId;
      if (id === "dev-test-customer") return id;
      return /^\d+$/.test(id) ? `gid://shopify/Customer/${id}` : id;
    }

    if (isDevEnvironment()) return "dev-test-customer";
    return null;
  }

  /**
   * キャッシュから登録者数を取得
   */
  function getCachedCount(productId) {
    const key = `favorite_count_${productId}`;
    const cached = localStorage.getItem(key);
    if (!cached) return null;
    try {
      const data = JSON.parse(cached);
      if (Date.now() - data.timestamp > CACHE_DURATION) {
        localStorage.removeItem(key);
        return null;
      }
      return data;
    } catch (e) {
      return null;
    }
  }

  /**
   * 登録者数をキャッシュに保存
   */
  function cacheCount(productId, count) {
    const key = `favorite_count_${productId}`;
    localStorage.setItem(key, JSON.stringify({ count, timestamp: Date.now() }));
  }

  /**
   * 初期化
   */
  function initProductDetailFavorites() {
    const productIdEl =
      document.querySelector("[data-product-id]") ||
      document.getElementById("data");
    const productId = productIdEl?.dataset.productId || productIdEl?.dataset.id;
    if (!productId) return;

    console.log("[WEGO Favorites] 初期化開始 - 商品ID:", productId);
    loadFavoriteStatus(productId);
    loadFavoriteCount(productId);
    setupEventListeners();
  }

  /**
   * お気に入り状態の読み込み
   */
  async function loadFavoriteStatus(productId) {
    const customerId = getCustomerId();
    if (!customerId || isDevEnvironment()) return;

    const buttons = document.querySelectorAll(
      ".vfa-wego-favorite-btn[data-variant-id]",
    );
    for (const button of buttons) {
      const variantId = button.dataset.variantId;
      if (!variantId) continue;
      try {
        const response = await fetch(`${API_BASE}/check`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ customerId, variantId }),
        });
        if (!response.ok) continue;
        const data = await response.json();
        if (data.success && data.isFavorited) {
          document
            .querySelectorAll(
              `.vfa-wego-favorite-btn[data-variant-id="${variantId}"]`,
            )
            .forEach((btn) => updateFavoriteButtonUI(btn, true));
        }
      } catch (error) {
        console.error("[WEGO Favorites] お気に入り状態確認エラー:", error);
      }
    }
  }

  /**
   * 登録者数の読み込み
   */
  async function loadFavoriteCount(productId, retryCount = 0) {
    const MAX_RETRIES = 3;
    const cached = getCachedCount(productId);
    if (cached) {
      updateFavoriteCountDisplay(productId, cached.count);
      return;
    }
    try {
      const controller = new AbortController();
      setTimeout(() => controller.abort(), 3000);
      const response = await fetch(
        `${COUNT_API_BASE}?productId=${encodeURIComponent(productId)}`,
        { signal: controller.signal },
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      if (data.success && data.data) {
        const count = data.data.totalCount || 0;
        updateFavoriteCountDisplay(productId, count);
        cacheCount(productId, count);
      }
    } catch (error) {
      if (retryCount < MAX_RETRIES) {
        await new Promise((resolve) =>
          setTimeout(resolve, 1000 * (retryCount + 1)),
        );
        return loadFavoriteCount(productId, retryCount + 1);
      }
      // 404 またはその他エラー時は 0 を表示
      updateFavoriteCountDisplay(productId, 0);
    }
  }

  /**
   * 登録者数表示を更新
   */
  function updateFavoriteCountDisplay(productId, count) {
    const displayValue =
      count === 0 || count === undefined || count === null
        ? "0"
        : count.toLocaleString();
    document
      .querySelectorAll(
        `.vfa-wego-favorite-count-wrapper[data-product-id="${productId}"] .vfa-wego-favorite-count-number`,
      )
      .forEach((el) => (el.textContent = displayValue));
    document
      .querySelectorAll(
        `.vfa-wego-favorite-count-wrapper[data-product-id="${productId}"]`,
      )
      .forEach((el) => (el.style.display = "flex"));
  }

  /**
   * イベントリスナーの設定
   */
  function setupEventListeners() {
    // SP版メインボタン
    document
      .querySelectorAll(
        ".vfa-wego-favorite-btn--sp.vfa-wego-favorite-btn--main",
      )
      .forEach((button) => {
        if (!button.dataset.wegoInitialized) {
          button.dataset.wegoInitialized = "true";
          button.addEventListener("click", handleSPMainButtonClick);
        }
      });

    // PC版スライダーボタン
    document.querySelectorAll(".vfa-js-favorite-new").forEach((button) => {
      if (!button.dataset.wegoInitialized) {
        button.dataset.wegoInitialized = "true";
        button.addEventListener("click", handlePCFavoriteButtonClick);
      }
    });

    // PC版スライダーお気に入りボタン
    document
      .querySelectorAll(".p-item-slider__popup-button.favorite")
      .forEach((button) => {
        if (!button.dataset.wegoInitialized) {
          button.dataset.wegoInitialized = "true";
          button.addEventListener("click", function (e) {
            e.preventDefault();
            e.stopPropagation();
            if (!getCustomerId()) {
              handleLoginRequiredClick(e);
              return;
            }
            showFavoriteDrawer();
          });
        }
      });

    // PC版バリアントボタン
    document
      .querySelectorAll(".vfa-p-item-sku-list__favorite.vfa-wego-favorite-btn")
      .forEach((button) => {
        if (!button.dataset.wegoInitialized) {
          button.dataset.wegoInitialized = "true";
          button.addEventListener("click", handleFavoriteClick);
        }
      });

    // 未ログインボタン
    document
      .querySelectorAll(".vfa-wego-favorite-btn--login-required")
      .forEach((button) => {
        if (!button.dataset.wegoInitialized) {
          button.dataset.wegoInitialized = "true";
          button.addEventListener("click", handleLoginRequiredClick);
        }
      });

    // イベント委譲
    document.addEventListener("click", function (e) {
      const button = e.target.closest(
        ".vfa-p-item-sku-list__favorite.vfa-wego-favorite-btn:not([data-wego-initialized])",
      );
      if (button) {
        button.dataset.wegoInitialized = "true";
        handleFavoriteClick(e);
      }
      const loginBtn = e.target.closest(
        ".vfa-wego-favorite-btn--login-required:not([data-wego-initialized])",
      );
      if (loginBtn) {
        loginBtn.dataset.wegoInitialized = "true";
        handleLoginRequiredClick(e);
      }
    });
  }

  function handlePCFavoriteButtonClick(e) {
    e.preventDefault();
    e.stopPropagation();
    if (
      e.currentTarget.classList.contains(
        "vfa-wego-favorite-btn--login-required",
      )
    ) {
      handleLoginRequiredClick(e);
      return;
    }
    showFavoriteDrawer();
  }

  function handleSPMainButtonClick(e) {
    e.preventDefault();
    e.stopPropagation();
    if (
      e.currentTarget.classList.contains(
        "vfa-wego-favorite-btn--login-required",
      )
    ) {
      handleLoginRequiredClick(e);
      return;
    }
    showFavoriteDrawer();
  }

  /**
   * お気に入りポップアップを表示
   */
  function showFavoriteDrawer() {
    const popup = document.querySelector(".vfa-js-wego-favorite-popup");
    if (popup) {
      // スクロール位置を保存
      popupState.oldPosition = window.scrollY || window.pageYOffset || 0;

      // bodyに固定スタイルを適用
      document.body.style.top = `-${popupState.oldPosition}px`;
      document.body.classList.add("fixed-side");

      // ポップアップを表示
      popup.style.display = "block";
      document.body.classList.add("vfa-popup-open");

      // visibleクラスを追加（アニメーション開始）
      setTimeout(function () {
        popup.classList.add("visible");
      }, 10);

      popup
        .querySelectorAll(".vfa-js-wego-favorite-close")
        .forEach((closeBtn) => {
          closeBtn.addEventListener("click", closeFavoriteDrawer);
        });
      popup
        .querySelectorAll(
          ".vfa-p-item-sku-list__favorite.vfa-wego-favorite-btn",
        )
        .forEach((button) => {
          if (!button.dataset.wegoInitialized) {
            button.dataset.wegoInitialized = "true";
            button.addEventListener("click", handleFavoriteClick);
          }
        });
      const productIdEl =
        document.querySelector("[data-product-id]") ||
        document.getElementById("data");
      const productId =
        productIdEl?.dataset.productId || productIdEl?.dataset.id;
      if (getCustomerId() && productId)
        loadDrawerFavoriteStatus(productId, popup);
    }
  }

  async function loadDrawerFavoriteStatus(productId, drawer) {
    const customerId = getCustomerId();
    if (!customerId || isDevEnvironment()) return;
    const buttons = drawer.querySelectorAll(
      ".vfa-wego-favorite-btn[data-variant-id]",
    );
    for (const button of buttons) {
      const variantId = button.dataset.variantId;
      if (!variantId) continue;
      try {
        const response = await fetch(`${API_BASE}/check`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ customerId, variantId }),
        });
        if (!response.ok) continue;
        const data = await response.json();
        if (data.success && data.isFavorited)
          updateFavoriteButtonUI(button, true);
      } catch (error) {
        console.error("[WEGO Favorites] ドロワー状態確認エラー:", error);
      }
    }
  }

  function closeFavoriteDrawer() {
    const popup = document.querySelector(".vfa-js-wego-favorite-popup");
    if (popup) {
      // bodyの固定スタイルを解除
      document.body.classList.remove("fixed-side");
      document.body.style.top = "";

      // visibleクラスを削除（アニメーション開始）
      popup.classList.remove("visible");

      // スクロール位置を復元
      setTimeout(function () {
        window.scrollTo(0, popupState.oldPosition);
        popup.style.display = "none";
        document.body.classList.remove("vfa-popup-open");
      }, 500);
    }
  }

  /**
   * お気に入りボタンクリック処理
   * 登録済み状態の場合は削除処理を実行しない（登録のみ可能）
   */
  async function handleFavoriteClick(e) {
    e.preventDefault();
    e.stopPropagation();
    const button =
      e.currentTarget || e.target.closest(".vfa-wego-favorite-btn");
    if (
      !button ||
      button.classList.contains("vfa-wego-favorite-btn--login-required")
    )
      return;

    // 登録済み状態の場合は処理を実行しない（削除不可）
    const isFavorited = button.classList.contains("vfa-is-favorited");
    if (isFavorited) {
      alert("すでにお気に入り登録されています");
      return;
    }

    const customerId = button.dataset.customerId || getCustomerId();
    if (
      !customerId ||
      (customerId === "dev-test-customer" && !isDevEnvironment())
    ) {
      handleLoginRequiredClick(e);
      return;
    }

    if (button.dataset.loading === "true") return;
    button.dataset.loading = "true";
    button.classList.add("vfa-is-loading");

    const productId = button.dataset.productId;
    const variantId = button.dataset.variantId;
    const action = "add"; // 常に追加のみ（削除は不可）

    updateFavoriteButtonUI(button, true); // 常に登録済み状態に更新

    try {
      const response = await fetch(`${API_BASE}/${action}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          customerId,
          productId,
          variantId,
          productName: button.dataset.productName || "",
          price: button.dataset.variantPrice || "",
          imageUrl: button.dataset.productImage || "",
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        updateFavoriteButtonUI(button, false); // エラー時は未登録状態に戻す
        showNotification("エラーが発生しました", "error");
        return;
      }
      const cached = getCachedCount(productId);
      if (cached) {
        originalCountMap[productId] = cached.count;
        const newCount = cached.count + 1; // 常に+1（削除は不可）
        updateFavoriteCountDisplay(productId, newCount);
        cacheCount(productId, newCount);
      } else {
        loadFavoriteCount(productId);
      }
      showNotification("お気に入りに追加しました", "success");
    } catch (error) {
      updateFavoriteButtonUI(button, false); // エラー時は未登録状態に戻す
      showNotification("エラーが発生しました", "error");
    } finally {
      button.dataset.loading = "false";
      button.classList.remove("vfa-is-loading");
    }
  }

  /**
   * お気に入りボタンのUIを更新（塗りつぶしアイコン対応）
   */
  function updateFavoriteButtonUI(button, isFavorited) {
    button.classList.toggle("vfa-is-favorited", isFavorited);

    // デフォルトアイコンとアクティブアイコンを探す
    const defaultIcon = button.querySelector(".vfa-favorite-icon--default");
    const activeIcon = button.querySelector(".vfa-favorite-icon--active");

    if (defaultIcon && activeIcon) {
      // 2つのアイコンがある場合は、表示/非表示を切り替える
      if (isFavorited) {
        defaultIcon.style.display = "none";
        activeIcon.style.display = "block";
      } else {
        defaultIcon.style.display = "block";
        activeIcon.style.display = "none";
      }
      return;
    }

    // SVGのpath要素を探す（インラインSVGの場合）
    const path = button.querySelector(".vfa-wego-favorite-icon path");
    if (path) {
      path.setAttribute("fill", isFavorited ? "#fa7283" : "none");
      path.setAttribute("stroke", "#fa7283");
      return;
    }

    // フォールバック: 単一のimgタグの場合（後方互換性のため）
    const img = button.querySelector(
      'img[src*="heart"], .c-icon img, .vfa-c-icon img, .vfa-wego-favorite-heart-icon',
    );
    if (img) {
      // お気に入り状態に応じてアイコンを切り替える
      if (isFavorited) {
        // heart-solid.svgに切り替え（既に#FA7283のピンク色）
        const currentSrc = img.src;
        if (
          currentSrc.includes("heart.svg") &&
          !currentSrc.includes("heart-solid.svg")
        ) {
          img.src = currentSrc.replace("heart.svg", "heart-solid.svg");
        }
        img.style.filter = "none";
        img.style.opacity = "1";
      } else {
        // heart.svgに戻す
        const currentSrc = img.src;
        if (currentSrc.includes("heart-solid.svg")) {
          img.src = currentSrc.replace("heart-solid.svg", "heart.svg");
        }
        img.style.filter = "none";
        img.style.opacity = "0.6";
      }
    }
  }

  function handleLoginRequiredClick(e) {
    e.preventDefault();
    e.stopPropagation();
    const returnUrl = encodeURIComponent(
      window.location.pathname + window.location.search,
    );
    window.location.href = `/account/login?return_url=${returnUrl}`;
  }

  function showNotification(message, type = "success") {
    const existing = document.querySelector(".vfa-wego-notification");
    if (existing) existing.remove();
    const notification = document.createElement("div");
    notification.className = `vfa-wego-notification vfa-wego-notification--${type}`;
    notification.textContent = message;
    const bgColor =
      { success: "#4caf50", error: "#f44336", info: "#2196f3" }[type] ||
      "#4caf50";
    notification.style.cssText = `position:fixed;top:20px;right:20px;background:${bgColor};color:white;padding:16px 24px;border-radius:4px;box-shadow:0 2px 8px rgba(0,0,0,0.2);z-index:10000;font-size:14px;`;
    document.body.appendChild(notification);
    setTimeout(() => notification.remove(), 3000);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initProductDetailFavorites);
  } else {
    initProductDetailFavorites();
  }
})();
